import type {
  AdminClientModule,
  ModuleRoute,
  ModuleRouteChrome,
  ModuleRouteLayout,
} from '@wit-support/ui-domain';

declare module '@tanstack/react-router' {
  interface StaticDataRouteOption {
    /**
     * Раскладка экрана в шелле ({@link ModuleRouteLayout}). Кладётся в
     * `staticData` маршрута модуля (`moduleRouteStaticData`), а шелл читает её из
     * совпавших маршрутов (`shellLayoutOf`) — без второго сопоставления путей
     * рядом с роутером.
     */
    layout?: ModuleRouteLayout;
    /**
     * Кто рисует шапку экрана ({@link ModuleRouteChrome}): маршрут, иначе
     * модуль (`routeChrome`), иначе `'page'`. Шелл читает через `shellChromeOf`.
     */
    chrome?: ModuleRouteChrome;
  }
}

/**
 * `staticData` маршрута модуля: раскладка (по умолчанию `'page'`) и хром —
 * свой у маршрута, иначе модуля (`routeChrome`), иначе `'page'`.
 */
export function moduleRouteStaticData(
  route: Pick<ModuleRoute, 'layout' | 'chrome'>,
  module?: Pick<AdminClientModule, 'routeChrome'>,
): { layout: ModuleRouteLayout; chrome: ModuleRouteChrome } {
  return {
    layout: route.layout ?? 'page',
    chrome: route.chrome ?? module?.routeChrome ?? 'page',
  };
}

/**
 * Хром текущего экрана по совпавшим маршрутам: решает самый глубокий, у
 * которого он задан. Ничего не задано — `'page'` (как было до хрома).
 */
export function shellChromeOf(
  matches: ReadonlyArray<{ staticData?: { chrome?: ModuleRouteChrome } | undefined }>,
): ModuleRouteChrome {
  for (let i = matches.length - 1; i >= 0; i--) {
    const chrome = matches[i]?.staticData?.chrome;
    if (chrome !== undefined) return chrome;
  }
  return 'page';
}

/**
 * Раскладка текущего экрана по совпавшим маршрутам (от корня к листу): решает
 * самый глубокий, у которого она задана. Ничего не задано — `'page'`.
 */
export function shellLayoutOf(
  matches: ReadonlyArray<{ staticData?: { layout?: ModuleRouteLayout } | undefined }>,
): ModuleRouteLayout {
  for (let i = matches.length - 1; i >= 0; i--) {
    const layout = matches[i]?.staticData?.layout;
    if (layout !== undefined) return layout;
  }
  return 'page';
}

/**
 * Классы контейнера шелла и `<main>` под раскладку.
 *
 * `'page'`: шелл растёт с содержимым (`min-h-dvh`), `<main>` прокручивается.
 * `'fullscreen'`: шелл ровно в высоту окна (`h-dvh`, единственная строка сетки
 * сжимаема — `minmax(0,1fr)`), `<main>` не прокручивается и не растёт — экран
 * получает фиксированную область и прокручивает свои панели сам.
 */
export function shellLayoutClasses(layout: ModuleRouteLayout): { root: string; main: string } {
  return layout === 'fullscreen'
    ? {
        root: 'h-dvh grid-rows-[minmax(0,1fr)] overflow-hidden',
        main: 'relative min-h-0 flex-1 overflow-hidden',
      }
    : layout === 'page-sticky'
      ? { root: 'min-h-dvh', main: 'flex-1 overflow-x-clip' }
      : { root: 'min-h-dvh', main: 'flex-1 overflow-auto' };
}
